import React, { useEffect, useState } from 'react';

import { observer } from 'mobx-react';
import { Container } from '@mui/material';
import { AxiosError } from 'axios';

import { angularizeWithProviders } from '../../../shared/providers';
import { BlackBgRow } from '../../../shared/components/ui-components';
import { useStepper } from '../../../shared/hooks/useStepper';
import { WelcomeStep } from './containers/WelcomeStep/WelcomeStep';
import { TwoFAStep } from './containers/TwoFAStep/TwoFAStep';
import { getAccessParamsFromUrl } from '../../../shared/utils/apiUtils';
import { entitySignUpStore } from '../../../shared/stores/entity/entitySignUpStore';
import { scanIqStore } from '../../stores/scanIqStore';
import { CreateCustomerStep } from './containers/CreateCustomerStep/CreateCustomerStep';
import { ExistCustomerStep } from './containers/ExistCustomerStep/ExistCustomerStep';
import { ScanIqHeader } from './components/ScanIqHeader/ScanIqHeader';
import { SETUP_STATUS } from '../../interfaces/ScanIqCustomer';
import { UnavailableWhenLoggedIn } from '../../../shared/containers/UnavailableWhenLoggedIn/UnavailableWhenLoggedIn';
import { entitySignUpService } from '../../../shared/services/entity/entitySignUpService';
import { ErrorState } from '../../../shared/components/ErrorState/ErrorState';
import { RESPONSE_STATUSES } from '../../../shared/constants/RESPONSE_STATUSES';
import { twoFAService, VERIFICATION_CHANNEL } from '../../../auth-management';

// this flow is available only for not logged in users (conflict with cookie)
const NgzInBodyScanIq = observer(() => {
  const { customer, setDefaultValues, setCustomer } = scanIqStore;
  const { entityId, setEntityData } = entitySignUpStore;

  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);

  const { activeStep, goToNextStep } = useStepper();

  const { accessParams, allParamsExist } = getAccessParamsFromUrl();
  const [entityIsInactive, setEntityIsInactive] = useState(false);

  const getEntityInfo = async () => {
    if (allParamsExist) {
      try {
        const entityData = await entitySignUpService.getScanIqEntityData(accessParams, {
          ignoreErrorCodes: [RESPONSE_STATUSES.INVALID],
        });

        setEntityData(entityData);
      } catch (error: unknown) {
        if (
          error instanceof AxiosError &&
          error.response?.status === RESPONSE_STATUSES.INVALID
        ) {
          setEntityIsInactive(true);
          return;
        }
        throw error;
      }
    }
  };

  const handleVerifyCode = async (consumer: string, code: string) => {
    if (!entityId) {
      throw new Error('Entity ID is not defined');
    }

    const scanIqCustomer = await twoFAService.verifyCodeForScanIq(
      {
        to: consumer,
        code,
        targetId: entityId,
      },
      { ignoreErrorCodes: [RESPONSE_STATUSES.VERIFICATION_CODE_INCORRECT] },
    );

    setCustomer(scanIqCustomer);
    goToNextStep();
  };

  useEffect(() => {
    getEntityInfo();

    return setDefaultValues;
  }, []);

  if (!allParamsExist) {
    return (
      <>
        <ScanIqHeader />
        <ErrorState
          title="We couldn't find this page"
          description={
            <p>
              Looks like the link might be incomplete or incorrect. Please double-check
              <br />
              the address and try again — or copy the full link from the original message.
            </p>
          }
        />
      </>
    );
  }

  if (entityIsInactive) {
    return (
      <>
        <ScanIqHeader />
        <ErrorState
          title="This entity isn't ready to accept customers yet"
          description={
            <p>
              It looks like this entity hasn&apos;t activated their account for selling
              services.
              <br />
              Please check back later or contact them directly if you have any questions.
            </p>
          }
        />
      </>
    );
  }

  const stepsComponents = [
    <WelcomeStep
      key="welcome-step"
      onSubmit={async (data) => {
        setPhoneNumber(data.phoneNumber);

        // This extra request is needed to ensure that the phone number is valid before moving the user to the next step.
        // We send the same request on the next step to get the verification code expiration time, but it does not trigger a second SMS while the current code has not expired.
        await twoFAService.sendVerificationCode(
          data.phoneNumber,
          VERIFICATION_CHANNEL.SMS,
        );

        goToNextStep();
      }}
    />,
    <TwoFAStep
      key="two-fa-step"
      phoneNumber={phoneNumber}
      onVerifyCode={handleVerifyCode}
    />,
    customer?.setupStatus === SETUP_STATUS.COMPLETED ? (
      <ExistCustomerStep key="exist-customer-step" />
    ) : (
      <CreateCustomerStep key="create-customer-step" />
    ),
  ];

  return (
    <>
      <ScanIqHeader />
      <UnavailableWhenLoggedIn>
        <Container maxWidth="sm">
          <BlackBgRow bgRowHeight="340px">{stepsComponents[activeStep]}</BlackBgRow>
        </Container>
      </UnavailableWhenLoggedIn>
    </>
  );
});

angularizeWithProviders(NgzInBodyScanIq, 'ngzInBodyScanIq', {});

export { NgzInBodyScanIq };
